import { createContext, useContext, useState } from "react";
import { useCookies } from "react-cookie";

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const storedToken = localStorage.getItem("site") || null;
  const [token, setToken] = useState(storedToken);

  // Extract username from JWT payload (base64-decoded, no verification needed)
  const getUserFromToken = (jwt) => {
    if (!jwt) return null;
    try {
      const payload = JSON.parse(atob(jwt.split(".")[1]));
      return payload.username || payload.sub || null;
    } catch {
      return null;
    }
  };

  const [user, setUser] = useState(getUserFromToken(storedToken));
  const [cookies, setCookies, removeCookie] = useCookies([
    "token_type",
    "access_token",
  ]);

  /** Clear all auth state — cookies, localStorage, sessionStorage, React state. */
  const clearAuthState = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("site");
    sessionStorage.removeItem("startVerified");
    removeCookie("token_type", { path: "/" });
    removeCookie("access_token", { path: "/" });
  };

  /**
   * Handles user login — never throws, always returns a structured result.
   * @param {Object} data - Login credentials { username, password }
   * @returns {Promise<{success: boolean, verified?: number, required?: number, unsealed?: boolean, token?: string, error?: string}>}
   */
  const loginAction = async (data) => {
    try {
      // Reset any stale auth cookie before authenticating. In a reused browser
      // window a previous session can leave a stale access_token cookie; if this
      // login doesn't return a token (non-final custodian in the unseal quorum),
      // that stale cookie would survive and later requests (getSecret reads the
      // cookie) would send it → 401 → spurious logout.
      removeCookie("token_type", { path: "/" });
      removeCookie("access_token", { path: "/" });

      const response = await fetch("/api/v1/bg/auth", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      const res = await response.json();

      if (!response.ok || res.errors) {
        const msg = res.errors
          ? res.errors[0].error
          : res.message || "Authentication failed";
        return { success: false, error: msg };
      }

      // if token returned (final custodian), store session with the fresh token
      if (res.token) {
        setUser(data.username);
        setToken(res.token);
        localStorage.setItem("site", res.token);
        setCookies("token_type", "Bearer", { path: "/" });
        setCookies("access_token", res.token, { path: "/" });
        sessionStorage.removeItem("startVerified");
      }

      return {
        success: true,
        verified: res.verified,
        required: res.required,
        unsealed: res.unsealed,
        token: res.token,
        pendingCode: res.pendingCode,
      };
    } catch (err) {
      console.error(err);
      return { success: false, error: err.message || "Network error occurred" };
    }
  };

  const logOut = () => {
    // Must also clear the cookies — the API layer (getSecret, getVaultKeyList,
    // keepalive, HttpService) authenticates from the access_token cookie, so a
    // cookie left behind here is a stale token that survives into the next
    // session in a reused window and causes 401 → logout loops.
    clearAuthState();
  };

  return (
    <AuthContext.Provider value={{ token, user, loginAction, logOut, cookies }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

export const GetAuth = () => {
  return useContext(AuthContext);
};
